import { Injectable } from '@angular/core';
import { translate } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';
import { formatNumber } from 'humanize-plus';

import moment from 'moment';

import pptxgen from 'pptxgenjs';

import { XIColors } from '@inmoment/the-kitchen';

import { EnableClassTelemetry } from 'app/services/otel.service';

import { Utils } from 'app/utils/utils';
import { LexDatePipe } from '../../../pipes/lex-date.pipe';
import { Story, StoryContent } from '../components/newspaper-widglet/journalists/journalist';

const SLIDE_LAYOUT = 'LAYOUT_WIDE' as const satisfies pptxgen['layout'];
const SLIDE_WIDTH_INCHES = 13.33;
const SLIDE_HEIGHT_INCHES = 7.5;
const FOOTER_HEIGHT_INCHES = 0.6;
const FOOTER_Y = SLIDE_HEIGHT_INCHES - FOOTER_HEIGHT_INCHES;

function getLogoObject(): pptxgen.ImageProps {
  const yTailHeight = 0.0625; // The lower part of the "y" seems to be about 1/16" and makes the logo appear off-center
  const widthHeightRatio = 1926.61 / 497.4; // Should match the ratio defined in the SVG's viewBox
  const h = FOOTER_HEIGHT_INCHES / 2;
  const w = widthHeightRatio * h;
  const remainingSpace = FOOTER_HEIGHT_INCHES - h;
  const offset = remainingSpace / 2 + yTailHeight / 2;
  const y = FOOTER_Y + offset;
  const x = SLIDE_WIDTH_INCHES - w - offset;

  return {
    path: '../../../assets/press-ganey/logo/svg/pg_logo_rev.svg',
    h,
    w,
    y,
    x,
  };
}

const DEFAULT_DECK_PROPS = { company: 'Press Ganey' };
const DEFAULT_CHART_OPTS = { x: 0.5, y: 0.5, w: 12, h: 6 };
const DEFAULT_IMAGE_OPTS = { x: 2, y: 0.75, w: 9, h: 6 };
const LEFT_COLUMN_X = 0.5;
const LEFT_COLUMN_WIDTH = 7.5;
const RIGHT_COLUMN_X = 8.5;
const RIGHT_COLUMN_WIDTH = SLIDE_WIDTH_INCHES - RIGHT_COLUMN_X - 0.5;
const IMAGE_HEIGHT = 2.6;
const TAG_PILL_CONFIG = {
  height: 0.3,
  minWidth: 0.6,
  charWidth: 0.12,
  horizontalPadding: 0.1,
  marginX: 0.15,
  rowGap: 0.12,
  fontSize: 11,
  fillColor: 'D8DDE5',
  textColor: '23313F',
} as const;
const MASTER_SLIDE: pptxgen.SlideMasterProps = {
  title: 'Spotlight Master Slide',
  background: { color: 'FFFFFF' },
  margin: [0.5, 0.25, 1.0, 0.25],
  slideNumber: { x: 0.6, y: 7.07, color: 'FFFFFF', fontFace: 'Arial', fontSize: 10 },
  objects: [
    {
      rect: {
        x: 0.0,
        y: FOOTER_Y,
        w: '100%',
        h: FOOTER_HEIGHT_INCHES,
        fill: { color: XIColors.orca.primary },
      },
    },
    { image: getLogoObject() },
    {
      text: {
        options: {
          x: 0,
          y: FOOTER_Y,
          w: '100%',
          h: FOOTER_HEIGHT_INCHES,
          align: 'center',
          valign: 'middle',
          color: 'FFFFFF',
          fontSize: 12,
        },
        text: 'Spotlight Dashboard',
      },
    },
  ],
};

interface AnyObject {
  [key: string]: any;
}

export interface SnapshotSlideObject {
  title: string;
  writtenAt: string;
  story: Story;
}

export interface InsightSlideContent {
  title: string;
  body?: string | pptxgen.TextProps[];
  tags?: string[];
  comments?: string;
  image?: pptxgen.ImageProps;
}

export type TableToSlideProps = Omit<pptxgen.TableToSlidesProps, 'masterSlideName'>;

export class PptxReport {
  private pptx: pptxgen;
  private properties: AnyObject;
  private lexDatePipe: LexDatePipe;

  constructor(properties: AnyObject = {}, lexDatePipe: LexDatePipe) {
    this.properties = Object.assign({}, DEFAULT_DECK_PROPS);
    this.properties = Object.assign(this.properties, properties);

    this.pptx = new pptxgen();
    this.pptx.layout = SLIDE_LAYOUT;

    if (this.properties.author) {
      this.pptx.author = this.properties.author;
    }
    if (this.properties.company) {
      this.pptx.company = this.properties.company;
    }
    if (this.properties.subject) {
      this.pptx.subject = this.properties.subject;
    }
    if (this.properties.title) {
      this.pptx.title = this.properties.title;
    }

    this.lexDatePipe = lexDatePipe;
    this.pptx.defineSlideMaster(MASTER_SLIDE);
    MASTER_SLIDE.title = translate('widgets.pptx.master-slide-title');
  }

  public addInsightTextSlide(content: InsightSlideContent): void {
    const slide = this.pptx.addSlide({ masterName: MASTER_SLIDE.title });
    const tagsHeading = 'Tags';
    const commentsHeading = 'Comments';

    // Left column: title, body
    let leftCursorY = 0.75;
    const bodyTopPadding = 0.1;
    const slideTitle = content.title;
    slide.addText(slideTitle, {
      x: LEFT_COLUMN_X,
      y: leftCursorY,
      w: LEFT_COLUMN_WIDTH,
      fontSize: 20,
      bold: true,
      align: 'left',
    });
    leftCursorY += 0.55;
    const formattedBody = this.formatInsightBodyText(content.body);
    if (formattedBody) {
      const bodyStartY = leftCursorY + bodyTopPadding;
      const bodyHeight = SLIDE_HEIGHT_INCHES - bodyStartY + FOOTER_HEIGHT_INCHES;
      slide.addText(formattedBody, {
        x: LEFT_COLUMN_X,
        y: bodyStartY,
        w: LEFT_COLUMN_WIDTH,
        h: bodyHeight,
        fontSize: 10,
        align: 'left',
        valign: 'top',
        color: '333333',
        lineSpacingMultiple: 1.15,
      });
    }

    // Right column: image, contact, tags, comments
    let rightCursorY = 0.75;

    if (content.image) {
      slide.addImage({
        ...content.image,
        x: content.image.x ?? RIGHT_COLUMN_X,
        y: content.image.y ?? rightCursorY,
        w: content.image.w ?? RIGHT_COLUMN_WIDTH,
        h: content.image.h ?? IMAGE_HEIGHT,
      });
    }
    rightCursorY += content.image ? IMAGE_HEIGHT + 0.3 : 0.75;

    if (content.tags?.length) {
      slide.addText(`${tagsHeading}:`, {
        x: RIGHT_COLUMN_X,
        y: rightCursorY,
        w: RIGHT_COLUMN_WIDTH,
        fontSize: 12,
        bold: true,
        color: '23313F',
      });
      rightCursorY += 0.35;

      const tagRows = this.layoutTagPills(content.tags, RIGHT_COLUMN_WIDTH);
      tagRows.forEach((row) => {
        row.forEach((pill) => {
          slide.addShape('roundRect', {
            fill: { color: TAG_PILL_CONFIG.fillColor },
            line: { color: TAG_PILL_CONFIG.fillColor },
            x: RIGHT_COLUMN_X + pill.x,
            y: rightCursorY,
            w: pill.width,
            h: TAG_PILL_CONFIG.height,
            rectRadius: TAG_PILL_CONFIG.height / 2,
          });
          slide.addText(pill.label, {
            x: RIGHT_COLUMN_X + pill.x,
            y: rightCursorY,
            w: pill.width,
            h: TAG_PILL_CONFIG.height,
            align: 'center',
            valign: 'middle',
            fontSize: TAG_PILL_CONFIG.fontSize,
            color: TAG_PILL_CONFIG.textColor,
          });
        });
        rightCursorY += TAG_PILL_CONFIG.height + TAG_PILL_CONFIG.rowGap;
      });

      rightCursorY += 0.1;
    }

    slide.addText(`${commentsHeading}:`, {
      x: RIGHT_COLUMN_X,
      y: rightCursorY,
      w: RIGHT_COLUMN_WIDTH,
      fontSize: 12,
      bold: true,
    });
    rightCursorY += 0.3;

    if (content.comments?.length) {
      const text = content.comments;
      slide.addText(text, {
        x: RIGHT_COLUMN_X,
        y: rightCursorY,
        w: RIGHT_COLUMN_WIDTH,
        fontSize: 11,
        color: '333333',
        lineSpacingMultiple: 1.05,
      });
      rightCursorY += 0.35;
    } else {
      slide.addText('No comments provided.', {
        x: RIGHT_COLUMN_X,
        y: rightCursorY,
        w: RIGHT_COLUMN_WIDTH,
        fontSize: 11,
        color: '777777',
      });
    }
  }

  private formatInsightBodyText(
    body?: string | pptxgen.TextProps[]
  ): string | pptxgen.TextProps[] | undefined {
    if (Array.isArray(body)) {
      return body;
    }

    const normalized = this.normalizeInsightBodyText(body);
    const lines = normalized
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const containsBoldMarker = lines.some((line) => /\*\*(.+?)\*\*/.test(line));
    if (!containsBoldMarker) {
      return lines.join('\n\n');
    }

    const textProps: pptxgen.TextProps[] = [];
    const defaultColor = '333333';
    const sectionRegex = /^\*\*(.+?)\*\*:?$/;
    const bulletRegex = /^\*\*(.+?)\*\*:(.+)$/;

    const pushText = (text: string, options?: pptxgen.TextProps['options']) => {
      textProps.push({ text, options });
    };

    lines.forEach((rawLine) => {
      if (!rawLine || rawLine === '-') {
        return;
      }

      const line = rawLine.replace(/^[-•]+\s*/, '').trim();
      if (!line) {
        return;
      }

      const bulletMatch = line.match(bulletRegex);
      const sectionMatch = bulletMatch ? null : line.match(sectionRegex);

      if (sectionMatch) {
        pushText(this.stripBoldMarkers(sectionMatch[0]), {
          bullet: { code: '25CF' },
          indentLevel: 0,
          bold: true,
          color: defaultColor,
          breakLine: true,
          lineSpacingMultiple: 1.1,
        });
        return;
      }

      if (bulletMatch) {
        const label = this.stripBoldMarkers(bulletMatch[1]).trim();
        const remainder = this.stripBoldMarkers(bulletMatch[2]).trim();

        pushText(`${label}:`, {
          bullet: { code: '25CB' },
          indentLevel: 1,
          bold: true,
          color: defaultColor,
          breakLine: !remainder,
        });

        if (remainder) {
          pushText(` ${remainder}`, {
            indentLevel: 1,
            color: defaultColor,
            breakLine: true,
          });
        }
        return;
      }

      pushText(this.stripBoldMarkers(line), {
        color: defaultColor,
        breakLine: true,
        bold: line.endsWith(':'),
      });
    });

    return textProps;
  }

  private normalizeInsightBodyText(body: string): string {
    let text = body.replace(/\r\n/g, '\n');
    text = text.replace(/#{2,6}\s*/g, '\n');
    text = text.replace(/([^\n])(\*\*[^*]+\*\*:)/g, (_match, prefix, boldBlock) => {
      return `${prefix}\n${boldBlock}`;
    });
    text = text.replace(/\*\*(.+?)\*\*/g, '**$1**');
    text = text.replace(/\n{2,}/g, '\n');
    return text;
  }

  private stripBoldMarkers(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, '$1');
  }

  private layoutTagPills(
    tags: string[],
    maxWidth: number
  ): { x: number; width: number; label: string }[][] {
    const rows: { x: number; width: number; label: string }[][] = [];
    let currentRow: { x: number; width: number; label: string }[] = [];
    let cursorX = 0;

    tags.forEach((label) => {
      const width = Math.max(
        TAG_PILL_CONFIG.minWidth,
        this.measureTagWidth(label) + TAG_PILL_CONFIG.horizontalPadding
      );

      if (cursorX + width > maxWidth && currentRow.length) {
        rows.push(currentRow);
        currentRow = [];
        cursorX = 0;
      }

      currentRow.push({ x: cursorX, width, label });
      cursorX += width + TAG_PILL_CONFIG.marginX;
    });

    if (currentRow.length) {
      rows.push(currentRow);
    }

    return rows;
  }

  private measureTagWidth(label: string): number {
    return label.length * TAG_PILL_CONFIG.charWidth;
  }

  public addTableSlide(
    data: AnyObject | AnyObject[],
    slideOptions?: AnyObject,
    filterSection = false
  ): void {
    slideOptions = Object.assign({}, slideOptions || {});

    const slide = this.pptx.addSlide({ masterName: MASTER_SLIDE.title });

    if (!(data instanceof Array)) {
      const row = data;
      if (row.text) {
        const textProps: pptxgen.TextProps = {
          text: row.text as string,
        };
        slide.addText([textProps], { x: 0.5, y: 0.25, w: 12, align: 'center', fontSize: 24 });
      }
      data = row.rows;
    }

    if (filterSection) {
      this.addFilterCountText(slide, slideOptions, data);
    }
    slide.addTable(data as pptxgen.TableRow[], slideOptions);
  }

  public addTextSlide(data: AnyObject, slideOptions?: AnyObject): void {
    if (data instanceof Array) {
      for (const textSlide of data) {
        this.addTextSlide(textSlide, slideOptions);
      }
      return;
    }

    const text = data.text || translate('widgets.pptx.no-widget-text');

    const slide = this.pptx.addSlide({ masterName: MASTER_SLIDE.title });
    slide.addText(text, {
      x: 0.5,
      y: 1.0,
      w: 12,
      h: 6,
      align: 'center',
      valign: 'top',
      autoFit: true,
      bold: true,
    });

    this.addSlideHeader(slide, data);

    this.addFilterCountText(slide, data);
  }

  public addSnapshotAsTextSlide(data: {
    title: string;
    snapshotData: SnapshotSlideObject;
    data;
    sentimentScale;
  }) {
    const slide = this.pptx.addSlide({ masterName: MASTER_SLIDE.title });
    const defaultOptions: any = {
      x: 0.5,
      y: 1.0,
      w: 12,
      h: '10%',
      valign: 'top',
      align: 'center',
      fit: 'shrink',
      bold: false,
      fontSize: 11,
      isTextBox: true,
    };
    let prevY = defaultOptions.y;
    Object.keys(data.snapshotData).forEach((key: keyof SnapshotSlideObject) => {
      switch (key) {
        case 'title':
          slide.addText(data.snapshotData[key], defaultOptions);
          break;
        case 'writtenAt':
          prevY = 1.25;
          slide.addText(data.snapshotData[key], { ...defaultOptions, y: prevY });
          break;
        case 'story':
          let text = '';
          prevY = 2;
          text += data.snapshotData[key].heading + '\n\n';
          data.snapshotData[key].contents.forEach((content: StoryContent) => {
            text += `${content.subHeader}\n\n${content.insight}${content?.insight ? '\n\n' : ''}`;
          });
          slide.addText(text, {
            ...defaultOptions,
            align: 'left',
            valign: 'top',
            y: prevY,
          });
          break;
      }
    });
    this.addSlideHeader(slide, data);
  }

  public addImageSlide(imageData: AnyObject, slideOptions: AnyObject, existingSlide?): any {
    imageData = Object.assign(Object.assign({}, DEFAULT_IMAGE_OPTS), imageData);

    const slide = existingSlide || this.pptx.addSlide({ masterName: MASTER_SLIDE.title });

    this.addSlideHeader(slide, slideOptions);

    this.addFilterCountText(slide, slideOptions);

    slide.addImage(imageData);

    return slide;
  }

  private addSlideHeader(slide: any, slideOptions: any) {
    if (slideOptions.title) {
      slide.addText(PptxReport.truncateTitle(slideOptions.title), {
        x: 0.5,
        y: 0.25,
        w: 12,
        align: 'center',
        fontSize: 24,
      });
    }
  }

  private static truncateTitle(title: string): string {
    const MAX_TITLE_LENGTH = 80;
    let result = title;

    if (result.length > MAX_TITLE_LENGTH) {
      result = result.substring(0, MAX_TITLE_LENGTH);

      result = `${result.substring(0, result.lastIndexOf(' '))}...`;
    }

    return result;
  }

  private getFilterCountText(slideOptions: any): string {
    if (slideOptions.data.docCount && slideOptions.data.maxDocs) {
      const percentage = ((slideOptions.data.docCount / slideOptions.data.maxDocs) * 100).toFixed(
        0
      );
      return translate('widgets.pptx.filtered-document-count', {
        filteredCount: slideOptions.data.docCount,
        totalCount: slideOptions.data.maxDocs,
        percentage,
        dataSource: slideOptions.data.dataSource,
      });
    }
    return '';
  }

  private addFilterCountText(slide: any, slideOptions: any, data?: AnyObject | AnyObject[]) {
    const displayText = this.getFilterCountText(slideOptions);
    if (data && displayText) {
      data.push([
        {
          text: displayText,
          options: {
            ...slideOptions,
            align: 'center',
            colspan: 2,
          },
        },
      ]);
      return;
    }

    slide.addText(displayText, {
      autofit: true,
      x: 0.5,
      y: '87%',
      w: 12,
      align: 'center',
      fontSize: 8,
    });
  }

  public addHighchartSlide(
    data: AnyObject | AnyObject[],
    slideOptions?: AnyObject,
    existingSlide?
  ): any {
    // TODO  - future refactor candidate -
    // proposal: add option that is passed from the consumer widget -> every widget specific logic could go there instead of here
    // that option should be an object that contains the widget specific logic
    // this should only make small modifications to add a chart slide
    if (data instanceof Array) {
      for (const hsSlide of data) {
        this.addHighchartSlide(hsSlide, slideOptions);
      }
      return;
    }

    const slide = existingSlide || this.pptx.addSlide({ masterName: MASTER_SLIDE.title });
    const highchartsData = data.data;

    let yAxis = highchartsData.yAxis || {};
    if (yAxis instanceof Array) {
      yAxis = yAxis[0];
    }

    let xAxis = highchartsData.xAxis || {};
    if (xAxis instanceof Array) {
      xAxis = xAxis[0];
    }

    if (xAxis.categories) {
      const categories = [];
      for (const categ of xAxis.categories) {
        categories.push(Utils.unescapeBlank(categ));
      }
      xAxis.categories = categories;
    }

    for (const seriesItem of highchartsData.series) {
      for (const dataItem of seriesItem.data) {
        dataItem.name = Utils.unescapeBlank(dataItem.name);
        dataItem.pointId = Utils.unescapeBlank(dataItem.pointId);
      }
    }

    const slideData = {
      type: 'bar',
      data: [[]],
      opts: Object.assign(
        Object.assign({ valAxes: [{} as any], catAxes: [{} as any] }, DEFAULT_CHART_OPTS),
        slideOptions || {}
      ),
    };

    const colorToHex = (rgbaStr) => {
      if (rgbaStr.includes('#')) {
        return rgbaStr;
      }
      const subStringStart = rgbaStr.includes('rgba') ? 5 : 4;

      const rgba = rgbaStr.substr(subStringStart).split(')')[0].split(',');

      let r = (+rgba[0]).toString(16);
      let g = (+rgba[1]).toString(16);
      let b = (+rgba[2]).toString(16);

      if (r.length === 1) {
        r = '0' + r;
      }
      if (g.length === 1) {
        g = '0' + g;
      }
      if (b.length === 1) {
        b = '0' + b;
      }

      return '#' + r + g + b;
    };

    if (!slideData.opts.chartColors && highchartsData.chart.type !== 'pie') {
      slideData.opts.chartColors = [[]];
      slideData.opts.invertedColors = [[]];

      const realSeries = highchartsData.series.filter(
        (series) => series.custom?.seriesType !== 'chartGoal'
      );

      const getColorForMultiAxesChartWithColorByPoint = (
        color: string,
        colorByPoint: boolean,
        index: number
      ): Array<string> => {
        if (!colorByPoint) {
          return [
            color
              ? colorToHex(color).replace('#', '')
              : getColorAsString(
                  Highcharts.defaultOptions.colors[index % Highcharts.defaultOptions.colors.length],
                  colorToHex
                ),
          ];
        }
        return highchartsData.series[index].data.map((d) => {
          if (d.color) {
            return colorToHex(d.color).replace('#', '');
          }
          return getColorAsString(Highcharts.defaultOptions.colors[0]).replace('#', '');
        });
      };
      if (realSeries.length > 1) {
        realSeries.forEach((series, index) => {
          const color = series.color
            ? colorToHex(series.color).replace('#', '')
            : getColorAsString(
                Highcharts.defaultOptions.colors[index % Highcharts.defaultOptions.colors.length],
                colorToHex
              );

          const chartColors = getColorForMultiAxesChartWithColorByPoint(
            color,
            highchartsData.plotOptions.series?.colorByPoint || series.colorByPoint,
            index
          );

          let insertIndex = series.yAxis ?? slideData.data.length;
          if (series.yAxis || (series?.type && series?.type !== slideData.type)) {
            // in theory instead of pushing i can just assign the color array to the intentional
            slideData.opts.chartColors[insertIndex] = slideData.opts.chartColors[insertIndex] || [];
            slideData.opts.chartColors[insertIndex] = [
              ...slideData.opts.chartColors[insertIndex],
              ...chartColors,
            ];
            slideData.opts.invertedColors[insertIndex] =
              slideData.opts.invertedColors[insertIndex] || [];
            slideData.opts.invertedColors[insertIndex] = [
              ...slideData.opts.invertedColors[insertIndex],
              ...chartColors,
            ];
          } else {
            // We need this because of sentiment polarity is handled differently
            const chartColorArrayAtIndex = slideData.opts.chartColors[insertIndex];
            if (!chartColorArrayAtIndex) {
              insertIndex = 0;
            }
            slideData.opts.chartColors[insertIndex] = [
              ...(slideData.opts.chartColors[insertIndex] || []),
              ...chartColors,
            ];
            slideData.opts.invertedColors[insertIndex] = [
              ...(slideData.opts.invertedColors[insertIndex] || []),
              ...chartColors,
            ];
          }
        });
      } else {
        const colors = highchartsData.series[0].data.map((d) => {
          if (d.color) {
            return colorToHex(d.color).replace('#', '');
          }
          return getColorAsString(Highcharts.defaultOptions.colors[0]).replace('#', '');
        });
        slideData.opts.chartColors[0] = colors;
        slideData.opts.invertedColors[0] = [...colors];
      }
    }

    // just to avoid compile error in new Highcharts version for colors
    function getColorAsString(color, rgbTransformer?: (c) => any): string {
      if (typeof color === 'string') {
        if (color.includes('rgb') && rgbTransformer) {
          return rgbTransformer(color).replace('#', '');
        }
        return color;
      }
      // we never reach this
      return '#000000';
    }

    if (xAxis.type === 'datetime') {
      slideData.opts.legendPos = 'b';
      slideData.opts.showLegend = true;

      const labelsMap = highchartsData.series
        .filter((series) => series.custom?.seriesType !== 'chartGoal')
        .map((series) => {
          return series.data.map((v) => {
            return v.x || v[0] || 0;
          });
        });

      let labelsArray = [];
      for (const arr of labelsMap) {
        labelsArray = labelsArray.concat(arr);
      }

      const uniqueLabels = Array.from(new Set(labelsArray));
      uniqueLabels.sort();

      let formatType: any = 'long';

      if (uniqueLabels.length > 1) {
        const daySpan = moment(uniqueLabels[1]).diff(moment(uniqueLabels[0]), 'days');
        if (daySpan > 7) {
          formatType = 'short';
        }
      }

      const labels = uniqueLabels.map((v) => this.lexDatePipe.transform(v, formatType));

      for (let index = 0; index < highchartsData.series.length; ++index) {
        const series = highchartsData.series[index];
        if (series.custom?.seriesType === 'chartGoal') {
          continue;
        }

        const seriesMap = {};
        for (const v of series.data) {
          const key = v.x || v[0];
          const value = v.y || v[1] || 0;
          seriesMap[key] = value;
        }

        const seriesData = uniqueLabels.map((v) => {
          return seriesMap.hasOwnProperty(v) ? seriesMap[v] : null;
        });

        const childSlide = {
          name: series.name || translate('widgets.pptx.nth-series', { index }),
          labels,
          values: seriesData,
        };

        if (series.yAxis) {
          slideData.data[series.yAxis] = slideData.data[series.yAxis] || [];
          slideData.data[series.yAxis].push(childSlide);
        } else {
          slideData.data[0].push(childSlide);
        }
      }
    } else if (highchartsData.chart.type === 'pie') {
      slideData.type = 'pie';
      slideData.opts.showLegend = true;
      slideData.opts.legendPos = 'r';
      slideData.opts.showLabel = false;
      slideData.opts.chartColors = [
        highchartsData.series[0].data.map((d) => colorToHex(d.color).replace('#', '')),
      ];
      slideData.opts.invertedColors = [[...slideData.opts.chartColors]];

      for (let index = 0; index < highchartsData.series.length; ++index) {
        const series = highchartsData.series[index];

        const filteredData = series.data.filter((d) => d.y);

        const childSlide = {
          name: series.name || translate('widgets.pptx.nth-series', { index }),
          labels: filteredData.map((d) => {
            if (highchartsData.isCustomDate) {
              return this.lexDatePipe.transform(d.name);
            }
            if (highchartsData.isCustomDouble) {
              return formatNumber(d.name, 2);
            }
            return d.pointDisplay || d.name;
          }),
          // need to undo prepareDataForPieChart
          values: filteredData.map((d) => {
            return d.y;
          }),
        };

        if (series.yAxis) {
          slideData.data[series.yAxis] = slideData.data[series.yAxis] || [];
          slideData.data[series.yAxis].push(childSlide);
        } else {
          slideData.data[0].push(childSlide);
        }
      }
      slideData.opts.catAxes[0].showLeaderLines = true;
      slideData.opts.catAxes[0].dataLabelPosition = 'bestFit';
    } else if (highchartsData.chart.type === 'scatter') {
      slideData.type = 'scatter';
      slideData.opts.showLegend = true;
      slideData.opts.legendPos = 'b';

      slideData.opts.valAxes[0].showCatAxisTitle = true;
      slideData.opts.valAxes[0].catAxisTitle = highchartsData.xAxis[0].title.text;

      const dataCollector = {};

      for (let index = 0; index < highchartsData.series.length; ++index) {
        const series = highchartsData.series[index];

        const x = series.data[0].x;
        const y = series.data[0].y;

        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          continue;
        }

        dataCollector[x] ||= { yValues: [] };

        dataCollector[x].yValues.push({
          value: y,
          label: series.name.replace('&', 'and'),
        });
      }

      const xValues = Object.keys(dataCollector);
      xValues.sort((x1, x2) => +x1 - +x2);
      const smallestXValueAsNumber = +xValues[0];
      let catAxisCrossValue = 0;
      if (smallestXValueAsNumber < 0) {
        catAxisCrossValue = smallestXValueAsNumber + -1;
      }
      slideData.opts.catAxisCrossesAt = catAxisCrossValue;

      const xLabel = {
        name: highchartsData.xAxis[0].title.text,
        values: [],
      };

      const yLabels = [];

      for (let index = 0; index < xValues.length; ++index) {
        const xValue = xValues[index];
        const xValueObj = dataCollector[xValue];

        xLabel.values.push(xValue);

        for (let j = 0; j < xValueObj.yValues.length; ++j) {
          const yValue = xValueObj.yValues[j];
          const yLabelValues = new Array(xValues.length);
          yLabelValues[index] = yValue.value;
          yLabels.push({
            name: yValue.label,
            values: yLabelValues,
            labels: [yValue.label],
          });
        }
      }

      slideData.data[0].push(xLabel);
      slideData.data[0].push(...yLabels);
    } else {
      let labels = xAxis.categories;

      const translatedTaxonomyLabels = [];
      if (highchartsData['taxonomyNodes']) {
        for (const label of labels) {
          const parsedLabel = label.split(':')[0];
          translatedTaxonomyLabels.push(
            highchartsData['taxonomyNodes'][parsedLabel]
              ? highchartsData['taxonomyNodes'][parsedLabel]['name']
              : parsedLabel
          );
        }
        labels = translatedTaxonomyLabels;
      }

      if (highchartsData.isCustomDate) {
        labels = labels.map((v) => this.lexDatePipe.transform(v));
      } else if (highchartsData.isCustomDouble) {
        labels = labels.map((v) => formatNumber(v, 2));
      }

      for (let index = 0; index < highchartsData.series.length; ++index) {
        const series = highchartsData.series[index];

        if (series.custom?.seriesType === 'chartGoal') {
          continue;
        }

        const childSlide: any = {
          name: series.name || translate('widgets.pptx.nth-series', { index }),
          labels: [...labels],
          values: series.data.map((d) => {
            if (typeof d !== 'number') {
              if (d === undefined) {
                return d;
              }
              return d.y || 0;
            } else {
              return d || 0;
            }
          }),
        };

        if (highchartsData.chart.type === 'bar') {
          childSlide.labels.reverse();
          childSlide.values.reverse();
        }

        if (series.yAxis || (series?.type && series?.type !== slideData.type)) {
          const insertIndex = series.yAxis ?? slideData.data.length;
          slideData.data[insertIndex] = slideData.data[insertIndex] || [];
          slideData.data[insertIndex].push(childSlide);
        } else {
          slideData.data[0].push(childSlide);
          slideData.opts.showLegend = slideData.data[0].length > 1;
          slideData.opts.legendPos = 'b';
        }
      }
    }

    const yTitle = yAxis && yAxis.title ? yAxis.title.text : '';

    if (yTitle) {
      slideData.opts.valAxes[0].showValAxisTitle = true;
      slideData.opts.valAxes[0].valAxisTitle = yTitle;
    }

    if (yAxis.max !== undefined) {
      slideData.opts.valAxes[0].valAxisMaxVal = yAxis.max;
    }

    if (highchartsData.plotOptions.series.stacking === 'percent') {
      slideData.opts.barGrouping = 'percentStacked';
      slideData.opts.valAxes[0].valAxisLabelFormatCode = '0%';
      slideData.opts.valAxes[0].valAxisMinVal = 0;
      slideData.opts.valAxes[0].valAxisMaxVal = 1;
    }
    slideData.opts.catAxes[0].catAxisLabelPos = 'low';
    if (highchartsData.chart.type === 'bar') {
      slideData.type = 'bar';
      slideData.opts.barDir = 'bar';
      if (!slideData.opts.barGrouping) {
        slideData.opts.chartColors[0].reverse();
        slideData.opts.invertedColors[0].reverse();
        slideData.data[0].reverse();
      }
    } else if (highchartsData.chart.type === 'line' || highchartsData.chart.type === 'spline') {
      slideData.type = 'line';
      if (highchartsData.chart.type === 'spline') {
        slideData.opts.lineSmooth = true;
      }
    } else if (highchartsData.chart.type === 'column') {
      slideData.type = 'bar';
      slideData.opts.barDir = 'col';
    } else if (highchartsData.chart.type === 'area') {
      slideData.type = 'area';
      if (highchartsData.plotOptions.series.stacking) {
        slideData.opts.barGrouping = 'stacked';
      }
    }

    if (highchartsData.plotOptions.series.stacking === 'normal') {
      slideData.opts.barGrouping = 'stacked';
    }

    // Multi-chart syntax does not work with single pie chart; see DASH-1257
    if (
      (highchartsData.yAxis instanceof Array && highchartsData.yAxis.length > 1) ||
      (highchartsData.series.some((s) => s?.type && s?.type !== slideData.type) &&
        highchartsData.series.every((s) => !s?.custom?.seriesType))
    ) {
      const getDataArray = (dataArray: Array<any>, shouldReverse = false) => {
        if (!shouldReverse) {
          return dataArray;
        }
        return dataArray.reverse();
      };
      const charts = [
        {
          type: this.pptx.ChartType[slideData.type || ''],
          data: getDataArray(slideData.data[0], slideOptions?.pptxOverride?.overrideDataReverse),
          options: {
            barGrouping: slideOptions.pptxOverride?.barGrouping
              ? slideOptions.pptxOverride.barGrouping
              : slideData.opts.barGrouping || 'stacked',
            chartColors: getDataArray(
              slideData.opts.chartColors[0],
              slideOptions?.pptxOverride?.overrideDataReverse
            ),
            invertedColors: getDataArray(
              slideData.opts.invertedColors[0],
              slideOptions?.pptxOverride?.overrideDataReverse
            ),
          },
        },
        {
          /**
           * Only change chart to line when data length > 1 -> data is box and we are not showing box as line
           */
          type:
            slideData.data[1].length === 1
              ? this.pptx.ChartType['line']
              : this.pptx.ChartType[slideData.type || ''],
          data: getDataArray(slideData.data[1], slideData.data[1].length > 1),
          options: {
            barGapWidthPct: 500,
            ...(slideData.data[1].length > 1 && {
              barGrouping: slideOptions.pptxOverride
                ? slideOptions.pptxOverride.barGrouping
                : slideData.opts.barGrouping || 'stacked',
            }),
            secondaryValAxis: true,
            secondaryCatAxis: true,
            chartColors: getDataArray(slideData.opts.chartColors[1], slideData.data[1].length > 1),
            invertedColors: getDataArray(
              slideData.opts.invertedColors[1],
              slideData.data[1].length > 1
            ),
          },
        },
      ];
      slideData.opts.valAxes[1] = {
        showValAxisTitle: true,
        valAxisTitle: highchartsData.yAxis[1]?.title ? highchartsData.yAxis[1].title.text : '',
      };
      if (highchartsData.yAxis[1]?.max !== undefined) {
        slideData.opts.valAxes[1].valAxisMaxVal = highchartsData.yAxis[1].max;
      }
      slideData.opts.catAxes[1] = {
        catAxisHidden: true,
      };
      slide.addChart(charts, slideData.opts);
    } else {
      slideData.opts = {
        ...slideData.opts,
        ...slideData.opts.valAxes[0],
        ...slideData.opts.catAxes[0],
      };

      delete slideData.opts.valAxes;
      delete slideData.opts.catAxes;

      if (slideData.opts.chartColors) {
        slideData.opts.chartColors = slideData.opts.chartColors[0];
      }
      if (slideData.opts.invertedColors) {
        slideData.opts.invertedColors = slideData.opts.invertedColors[0];
      }
      if (
        slideData.opts.barGrouping === 'percentStacked' ||
        slideData.opts.barGrouping === 'stacked'
      ) {
        slideData.data[0].reverse();
        if (data.data.plotOptions.series.stacking !== 'normal' || slideData.opts.barDir !== 'bar') {
          slideData.opts.chartColors.reverse();
        }
      }

      slide.addChart(this.pptx.ChartType[slideData.type || ''], slideData.data[0], slideData.opts);
    }

    this.addSlideHeader(slide, data);

    return slide;
  }

  public save(fileName: string): void {
    this.pptx.writeFile({ fileName });
  }

  tableToSlides(eleId: string, props?: TableToSlideProps): void {
    this.pptx.tableToSlides(eleId, { ...(props ?? {}), masterSlideName: MASTER_SLIDE.title });
  }
}

@EnableClassTelemetry()
@Injectable()
export class PptxService {
  constructor(private lexDatePipe: LexDatePipe) {}

  public createPptx(properties: AnyObject = {}): PptxReport {
    return new PptxReport(properties, this.lexDatePipe);
  }
}
